//trigger deployment
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Button, Card, Col, Image, Layout, List, Row, Space, Spin, Tag, Typography } from 'antd';
import { format } from 'date-fns';

import { AdminService } from '../services/AdminService';

const { Content } = Layout;
const { Title, Text } = Typography;

interface SessionData {
  id: string;
  productSize: string;
  bleed: string;
  sidesCount: number;
  productType: string;
  quantity: number;
  conneoId: string;
  customerName: string | null;
  confirmation: string;
  createdAt: string;
  updatedAt: string;
}

interface Product {
  id: string;
  count: number;
  name: string;
  sessionId: string;
  createdAt: string;
  updatedAt: string;
}

interface Asset {
  id: string;
  url: string;
  previewFile: string;
  previewImage: string;
  dimensions: string;
  pageNumber: number;
  filename: string;
  assetFilename: string;
  metadata: string;
  sessionId: string;
  createdAt: string;
}

interface Side {
  id: string;
  actions: string;
  cropArea: string;
  resizeMetadata: string;
  orderNumber: number;
  previewImageUrl: string;
  assetId: string;
  productId: string;
  sessionId: string;
  createdAt: string;
  updatedAt: string;
  assetPreviewImage: string;
  assetFilename: string;
}

const Sessions: React.FC = () => {
  const { t } = useTranslation();
  const [selectedSession, setSelectedSession] = useState<SessionData | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50);

  // Fetch sessions
  const {
    data: sessionsData,
    isLoading: sessionsLoading,
    error: sessionsError,
  } = useQuery(
    ['admin-sessions', currentPage, pageSize],
    () => AdminService.getSessions(currentPage, pageSize),
    {
      refetchInterval: 30000, // Refresh every 30 seconds
      keepPreviousData: true, // Keep previous data while loading new page
    },
  );

  // Fetch products when session is selected
  const { data: productsData, isLoading: productsLoading } = useQuery(
    ['admin-products', selectedSession?.id],
    () => AdminService.getProducts(selectedSession!.id),
    {
      enabled: !!selectedSession,
    },
  );

  // Fetch sides when product is selected
  const { data: sidesData, isLoading: sidesLoading } = useQuery(
    ['admin-sides', selectedProduct?.id],
    () => AdminService.getSides(selectedProduct!.id),
    {
      enabled: !!selectedProduct,
    },
  );

  const sessions = sessionsData?.sessions || [];
  const pagination = sessionsData?.pagination;
  const products = productsData?.products || [];
  const sides = sidesData?.sides || [];

  const handleSessionSelect = (session: SessionData) => {
    setSelectedSession(session);
    setSelectedProduct(null); // Reset product selection
  };

  const handleProductSelect = (product: Product) => {
    setSelectedProduct(product);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setSelectedSession(null); // Reset selection when changing pages
    setSelectedProduct(null);
  };

  // Auto-select first product when products are loaded
  React.useEffect(() => {
    if (products.length > 0 && !selectedProduct) {
      setSelectedProduct(products[0]);
    }
  }, [products, selectedProduct]);

  if (sessionsLoading) {
    return (
      <Layout style={{ minHeight: '100vh' }}>
        <Content
          style={{
            padding: '24px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Spin size="large" />
        </Content>
      </Layout>
    );
  }

  if (sessionsError) {
    return (
      <Layout style={{ minHeight: '100vh' }}>
        <Content style={{ padding: '24px' }}>
          <Card>
            <Text type="danger">Error loading sessions data</Text>
          </Card>
        </Content>
      </Layout>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Content style={{ padding: '24px' }}>
        <Title level={2} style={{ marginBottom: '24px' }}>
          {t('navigation.sessions')}
        </Title>

        <Row gutter={[16, 16]} style={{ height: '600px' }}>
          {/* Sessions List - Left Column */}
          <Col span={8}>
            <Card
              title={
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <span>Sessions</span>
                  {pagination && (
                    <span style={{ fontSize: '12px', color: '#666' }}>
                      {pagination.total} total
                    </span>
                  )}
                </div>
              }
              style={{ height: '100%' }}
              styles={{ body: { height: '520px', overflow: 'auto', padding: '8px' } }}
            >
              <List
                dataSource={sessions}
                renderItem={(session: SessionData) => (
                  <List.Item
                    onClick={() => handleSessionSelect(session)}
                    style={{
                      cursor: 'pointer',
                      backgroundColor:
                        selectedSession?.id === session.id ? '#f0f8ff' : 'transparent',
                      padding: '12px',
                      borderRadius: '4px',
                      marginBottom: '4px',
                    }}
                  >
                    <div style={{ width: '100%' }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                        {session.id.substring(0, 8)}...
                      </div>
                      <div
                        style={{
                          fontSize: '12px',
                          color: '#1890ff',
                          fontWeight: 'bold',
                          marginBottom: '2px',
                        }}
                      >
                        Conneo ID: {session.conneoId}
                        {session.customerName ? ` (${session.customerName})` : ''}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {format(new Date(session.createdAt), 'dd/MM/yyyy HH:mm')}
                      </div>
                      <Space size="small" style={{ marginTop: '4px' }}>
                        <Tag>{session.productSize}</Tag>
                        <Tag>Qty: {session.quantity}</Tag>
                        <Tag>{session.sidesCount} sides</Tag>
                      </Space>
                    </div>
                  </List.Item>
                )}
              />
              {pagination && (pagination.hasNext || pagination.hasPrev) && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: '0',
                    left: '0',
                    right: '0',
                    padding: '8px',
                    borderTop: '1px solid #f0f0f0',
                    backgroundColor: '#fff',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Button
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(1)}
                      size="small"
                    >
                      First
                    </Button>
                    <Button
                      disabled={!pagination.hasPrev}
                      onClick={() => handlePageChange(currentPage - 1)}
                      size="small"
                    >
                      Previous
                    </Button>
                  </div>
                  <span>Page {currentPage}</span>
                  <Button
                    disabled={!pagination.hasNext}
                    onClick={() => handlePageChange(currentPage + 1)}
                    size="small"
                  >
                    Next
                  </Button>
                </div>
              )}
            </Card>
          </Col>

          {/* Products List - Middle Column */}
          <Col span={8}>
            <Card
              title="Products"
              style={{ height: '100%' }}
              styles={{ body: { height: '520px', overflow: 'auto', padding: '8px' } }}
            >
              {selectedSession ? (
                productsLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Spin />
                    <div style={{ marginTop: '8px', color: '#666' }}>Loading products...</div>
                  </div>
                ) : (
                  <List
                    dataSource={products}
                    renderItem={(product: Product) => (
                      <List.Item
                        onClick={() => handleProductSelect(product)}
                        style={{
                          cursor: 'pointer',
                          backgroundColor:
                            selectedProduct?.id === product.id ? '#f0f8ff' : 'transparent',
                          padding: '12px',
                          borderRadius: '4px',
                          marginBottom: '4px',
                        }}
                      >
                        <div style={{ width: '100%' }}>
                          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                            {product.name || 'Product'}
                          </div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            Count: {product.count || 1}
                          </div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {format(new Date(product.createdAt), 'dd/MM/yyyy HH:mm')}
                          </div>
                        </div>
                      </List.Item>
                    )}
                  />
                )
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                  Select a session to view products
                </div>
              )}
            </Card>
          </Col>

          {/* Sides List - Right Column */}
          <Col span={8}>
            <Card
              title="Sides"
              style={{ height: '100%' }}
              styles={{ body: { height: '520px', overflow: 'auto', padding: '8px' } }}
            >
              {selectedProduct ? (
                sidesLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Spin />
                    <div style={{ marginTop: '8px', color: '#666' }}>Loading sides...</div>
                  </div>
                ) : (
                  <List
                    dataSource={sides}
                    renderItem={(side: Side) => (
                      <List.Item
                        style={{
                          padding: '12px',
                          borderRadius: '4px',
                          marginBottom: '8px',
                          border: '1px solid #f0f0f0',
                        }}
                      >
                        <div style={{ width: '100%' }}>
                          <Row gutter={12}>
                            <Col span={8}>
                              {side.previewImageUrl && (
                                <Image
                                  src={side.previewImageUrl}
                                  alt="Side preview"
                                  style={{
                                    width: '100%',
                                    height: '60px',
                                    objectFit: 'cover',
                                    borderRadius: '4px',
                                  }}
                                  fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYSNbOMYbRvSwMGmExa2kRGOAiVDOAMkHAFSBkgGSBggGSAZ4A2QDJAMkAxYBvQmXHvdX03PTM/0dHf1W+9T1dXV3V/9v+d7q/t7q5f+5P9c/2p7r/7vDgAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABgKv76j2++f7t7++37d26+e/WL1/5u/Jtz8+3q9//3o++G78WqAwAAAAA="
                                />
                              )}
                            </Col>
                            <Col span={16}>
                              <div style={{ fontSize: '12px' }}>
                                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                  Side {side.orderNumber || 'N/A'}
                                </div>
                                {side.assetFilename && (
                                  <div style={{ color: '#666', marginBottom: '2px' }}>
                                    File: {side.assetFilename}
                                  </div>
                                )}
                                <div style={{ color: '#666' }}>
                                  {format(new Date(side.createdAt), 'dd/MM/yyyy HH:mm')}
                                </div>
                              </div>
                            </Col>
                          </Row>
                        </div>
                      </List.Item>
                    )}
                  />
                )
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                  Select a product to view sides
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </Content>
    </Layout>
  );
};

export default Sessions;
